import {
  Clock,
  Color,
  Matrix4,
  Mesh,
  RepeatWrapping,
  ShaderMaterial,
  TextureLoader,
  UniformsLib,
  UniformsUtils,
  Vector2,
  Vector4,
} from 'three';
import { Reflector } from './Reflector.js';
import { Refractor } from './Refractor.js';

/** @module Water2 */

/**
 * An advanced water effect that supports reflections, refractions and flow maps.
 *
 * Note that this class can only be used with {@link WebGLRenderer}.
 * When using {@link WebGPURenderer}, use {@link module:Water2Mesh}.
 *
 * References:
 *
 * - {@link https://alex.vlachos.com/graphics/Vlachos-SIGGRAPH10-WaterFlow.pdf}
 * - {@link http://graphicsrunner.blogspot.de/2010/08/water-using-flow-maps.html}
 *
 * @augments Mesh
 * @three_import import { Water } from 'three/addons/objects/Water2.js';
 */
class Water extends Mesh {
  /**
   * Constructs a new water instance.
   *
   * @param {BufferGeometry} geometry - The water's geometry.
   * @param {module:Water2~Options} [options] - The configuration options.
   */
  constructor(geometry, options = {}) {
    super(geometry);

    /**
     * This flag can be used for type testing.
     *
     * @type {boolean}
     * @readonly
     * @default true
     */
    this.isWater = true;

    this.type = 'Water';

    const scope = this;

    const color =
      options.color !== undefined
        ? new Color(options.color)
        : new Color(0xffffff);
    const opacity = options.opacity !== undefined ? options.opacity : 1;
    const textureWidth =
      options.textureWidth !== undefined ? options.textureWidth : 512;
    const textureHeight =
      options.textureHeight !== undefined ? options.textureHeight : 512;
    const clipBias = options.clipBias !== undefined ? options.clipBias : 0;
    const flowDirection =
      options.flowDirection !== undefined
        ? options.flowDirection
        : new Vector2(1, 0);
    const flowSpeed =
      options.flowSpeed !== undefined ? options.flowSpeed : 0.03;
    const reflectivity =
      options.reflectivity !== undefined ? options.reflectivity : 0.02;
    const scale = options.scale !== undefined ? options.scale : 1;
    const shader =
      options.shader !== undefined ? options.shader : Water.WaterShader;
    const rttUpdateInterval =
      options.rttUpdateInterval !== undefined ? options.rttUpdateInterval : 0;

    const textureLoader = new TextureLoader();

    const flowMap = options.flowMap || undefined;
    const normalMap0 =
      options.normalMap0 || textureLoader.load('textures/Water_1_M_Normal.jpg');
    const normalMap1 =
      options.normalMap1 || textureLoader.load('textures/Water_2_M_Normal.jpg');

    const cycle = 0.15; // a cycle of a flow map phase
    const halfCycle = cycle * 0.5;
    const textureMatrix = new Matrix4();
    const clock = new Clock();
    let lastRttUpdateMs = -Infinity;

    const nowMs = () =>
      typeof performance !== 'undefined' &&
      typeof performance.now === 'function'
        ? performance.now()
        : Date.now();

    // internal components

    if (Reflector === undefined) {
      console.error('THREE.Water: Required component Reflector not found.');
      return;
    }

    if (Refractor === undefined) {
      console.error('THREE.Water: Required component Refractor not found.');
      return;
    }

    const reflector = new Reflector(geometry, {
      textureWidth: textureWidth,
      textureHeight: textureHeight,
      clipBias: clipBias,
      multisample: options.multisample,
      // Always update reflection RTT even when camera is static.
      // The river overlay can look like it "loses segments" if reflection becomes stale
      // while normals/flow continue animating.
      skipStatic: false,
    });

    const refractor = new Refractor(geometry, {
      textureWidth: textureWidth,
      textureHeight: textureHeight,
      clipBias: clipBias,
      multisample: options.multisample,
    });

    reflector.matrixAutoUpdate = false;
    refractor.matrixAutoUpdate = false;

    // material

    this.material = new ShaderMaterial({
      name: shader.name,
      uniforms: UniformsUtils.merge([UniformsLib['fog'], shader.uniforms]),
      vertexShader: shader.vertexShader,
      fragmentShader: shader.fragmentShader,
      transparent: true,
      fog: true,
    });

    this.material.uniforms['opacity'].value = opacity;

    if (flowMap !== undefined) {
      this.material.defines.USE_FLOWMAP = '';
      this.material.uniforms['tFlowMap'] = {
        type: 't',
        value: flowMap,
      };
      this.material.defines.USE_FLOWMASK = '';
      this.material.uniforms['maskCutoff'] = {
        type: 'f',
        value: options.maskCutoff !== undefined ? options.maskCutoff : 0.5,
      };
    } else {
      this.material.uniforms['flowDirection'] = {
        type: 'v2',
        value: flowDirection,
      };
    }

    // maps

    normalMap0.wrapS = normalMap0.wrapT = RepeatWrapping;
    normalMap1.wrapS = normalMap1.wrapT = RepeatWrapping;

    this.material.uniforms['tReflectionMap'].value =
      reflector.getRenderTarget().texture;
    this.material.uniforms['tRefractionMap'].value =
      refractor.getRenderTarget().texture;
    this.material.uniforms['tNormalMap0'].value = normalMap0;
    this.material.uniforms['tNormalMap1'].value = normalMap1;

    // water

    this.material.uniforms['color'].value = color;
    this.material.uniforms['reflectivity'].value = reflectivity;
    this.material.uniforms['textureMatrix'].value = textureMatrix;

    // // initial values

    // this.material.uniforms["config"].value.x = 0; // flowMapOffset0
    // this.material.uniforms["config"].value.y = halfCycle; // flowMapOffset1
    // this.material.uniforms["config"].value.z = halfCycle; // halfCycle
    // this.material.uniforms["config"].value.w = scale; // scale

    const configUniform =
      this.material.uniforms['config'] ??
      (this.material.uniforms['config'] = { type: 'v4', value: new Vector4() });

    configUniform.value.x = 0;
    configUniform.value.y = halfCycle;
    configUniform.value.z = halfCycle;
    configUniform.value.w = scale;

    // functions

    function updateTextureMatrix(camera) {
      textureMatrix.set(
        0.5,
        0.0,
        0.0,
        0.5,
        0.0,
        0.5,
        0.0,
        0.5,
        0.0,
        0.0,
        0.5,
        0.5,
        0.0,
        0.0,
        0.0,
        1.0
      );

      textureMatrix.multiply(camera.projectionMatrix);
      textureMatrix.multiply(camera.matrixWorldInverse);
      textureMatrix.multiply(scope.matrixWorld);
    }

    function updateFlow() {
      const delta = clock.getDelta();
      const config = scope.material.uniforms['config'];

      config.value.x += flowSpeed * delta; // flowMapOffset0
      config.value.y = config.value.x + halfCycle; // flowMapOffset1

      // Important: The distance between offsets should be always the value of "halfCycle".
      // Moreover, both offsets should be in the range of [ 0, cycle ].
      // This approach ensures a smooth water flow and avoids "reset" effects.

      if (config.value.x >= cycle) {
        config.value.x = 0;
        config.value.y = halfCycle;
      } else if (config.value.y >= cycle) {
        config.value.y = config.value.y - cycle;
      }
    }

    //

    this.onBeforeRender = function (renderer, scene, camera) {
      // Prevent nested RTT updates when rendering from reflector/refractor virtual cameras.
      // Without this, multiple Water meshes can trigger expensive recursive renders.
      if (
        camera?.userData?.refractor === true ||
        camera?.userData?.reflector === true
      ) {
        return;
      }

      updateTextureMatrix(camera);
      updateFlow();

      if (rttUpdateInterval > 0) {
        const now = nowMs();
        if (now - lastRttUpdateMs < rttUpdateInterval * 1000) return;
        lastRttUpdateMs = now;
      }

      scope.visible = false;

      reflector.matrixWorld.copy(scope.matrixWorld);
      refractor.matrixWorld.copy(scope.matrixWorld);

      // Ensure reflector updates even when view normal check would skip it.
      reflector.forceUpdate = true;

      reflector.onBeforeRender(renderer, scene, camera);
      refractor.onBeforeRender(renderer, scene, camera);

      scope.visible = true;
    };
  }
}

Water.WaterShader = {
  name: 'WaterShader',

  uniforms: {
    color: {
      type: 'c',
      value: null,
    },
    opacity: {
      value: 1,
    },

    reflectivity: {
      type: 'f',
      value: 0,
    },

    tReflectionMap: {
      type: 't',
      value: null,
    },

    tRefractionMap: {
      type: 't',
      value: null,
    },

    tNormalMap0: {
      type: 't',
      value: null,
    },

    tNormalMap1: {
      type: 't',
      value: null,
    },

    textureMatrix: {
      type: 'm4',
      value: null,
    },

    config: {
      type: 'v4',
      value: new Vector4(),
    },
  },

  vertexShader: /* glsl */ `

		#include <common>
		#include <fog_pars_vertex>
		#include <logdepthbuf_pars_vertex>

		uniform mat4 textureMatrix;

		varying vec4 vCoord;
		varying vec2 vUv;
		varying vec3 vToEye;

		void main() {

			vUv = uv;
			vCoord = textureMatrix * vec4( position, 1.0 );

			vec4 worldPosition = modelMatrix * vec4( position, 1.0 );
			vToEye = cameraPosition - worldPosition.xyz;

			vec4 mvPosition =  viewMatrix * worldPosition; // used in fog_vertex
			gl_Position = projectionMatrix * mvPosition;

			#include <logdepthbuf_vertex>
			#include <fog_vertex>

		}`,

  fragmentShader: /* glsl */ `

		#include <common>
		#include <fog_pars_fragment>
		#include <logdepthbuf_pars_fragment>

		uniform sampler2D tReflectionMap;
		uniform sampler2D tRefractionMap;
		uniform sampler2D tNormalMap0;
		uniform sampler2D tNormalMap1;

		#ifdef USE_FLOWMAP
			uniform sampler2D tFlowMap;
			uniform float maskCutoff;
		#else
			uniform vec2 flowDirection;
		#endif

		uniform vec3 color;
		uniform float opacity;
		uniform float reflectivity;
		uniform vec4 config;

		varying vec4 vCoord;
		varying vec2 vUv;
		varying vec3 vToEye;

		void main() {

			#include <logdepthbuf_fragment>

			float flowMapOffset0 = config.x;
			float flowMapOffset1 = config.y;
			float halfCycle = config.z;
			float scale = config.w;

			vec3 toEye = normalize( vToEye );

			vec2 flow;
      float flowMask = 1.0;
			#ifdef USE_FLOWMAP
				vec4 flowSample = texture2D( tFlowMap, vUv );
				flow = flowSample.rg * 2.0 - 1.0;
				#ifdef USE_FLOWMASK
          // Stable mask sampling for thin/aliased river strokes:
          // take the max alpha in a small neighborhood based on the pixel footprint.
          // This avoids segments disappearing when the camera is still due to minification/aliasing.
          vec2 duv = max( fwidth( vUv ) * 0.25, vec2( 1.0 / 4096.0 ) );
          float a = flowSample.a;
          a = max( a, texture2D( tFlowMap, vUv + vec2( duv.x, 0.0 ) ).a );
          a = max( a, texture2D( tFlowMap, vUv - vec2( duv.x, 0.0 ) ).a );
          a = max( a, texture2D( tFlowMap, vUv + vec2( 0.0, duv.y ) ).a );
          a = max( a, texture2D( tFlowMap, vUv - vec2( 0.0, duv.y ) ).a );

          // Keep edges crisp (similar thickness as the original hard cutoff),
          // but still avoid hard discard holes.
          flowMask = smoothstep( maskCutoff, maskCutoff + 0.006, a );
          flowMask = pow( flowMask, 2.2 );
				#endif
			#else
				flow = flowDirection;
			#endif
			flow.x *= - 1.0;

			// sample normal maps (distort uvs with flowdata)
			vec4 normalColor0 = texture2D( tNormalMap0, ( vUv * scale ) + flow * flowMapOffset0 );
			vec4 normalColor1 = texture2D( tNormalMap1, ( vUv * scale ) + flow * flowMapOffset1 );

			// linear interpolate to get the final normal color
			float flowLerp = abs( halfCycle - flowMapOffset0 ) / halfCycle;
			vec4 normalColor = mix( normalColor0, normalColor1, flowLerp );

			// calculate normal vector
			vec3 normal = normalize( vec3( normalColor.r * 2.0 - 1.0, normalColor.b,  normalColor.g * 2.0 - 1.0 ) );

			// calculate the fresnel term to blend reflection and refraction maps
			float theta = max( dot( toEye, normal ), 0.0 );
			float reflectance = reflectivity + ( 1.0 - reflectivity ) * pow( ( 1.0 - theta ), 5.0 );

			// calculate final uv coords
			vec3 coord = vCoord.xyz / vCoord.w;
			vec2 uv = coord.xy + coord.z * normal.xz * 0.05;

			vec4 reflectColor = texture2D( tReflectionMap, vec2( 1.0 - uv.x, uv.y ) );
			vec4 refractColor = texture2D( tRefractionMap, uv );

			// multiply water color with the mix of both textures
			gl_FragColor = vec4( color, 1.0 ) * mix( refractColor, reflectColor, reflectance );
      gl_FragColor.a *= opacity * flowMask;

            #include <tonemapping_fragment>
            #include <encodings_fragment>
            #include <fog_fragment>

		}`,
};

/**
 * Constructor options of `Water`.
 *
 * @typedef {Object} module:Water2~Options
 * @property {number|Color|string} [color=0xFFFFFF] - The water color.
 * @property {number} [textureWidth=512] - The texture width. A higher value results in better quality but is also more expensive.
 * @property {number} [textureHeight=512] - The texture height. A higher value results in better quality but is also more expensive.
 * @property {number} [clipBias=0] - The clip bias.
 * @property {Vector2} [flowDirection=(1,0)] - The water's flow direction.
 * @property {number} [flowSpeed=0.03] - The water's flow speed.
 * @property {number} [reflectivity=0.02] - The water's reflectivity.
 * @property {number} [scale=1] - The water's scale.
 * @property {number} [rttUpdateInterval=0] - Minimum seconds between reflection/refraction RTT updates. `0` updates every frame.
 * @property {Object} [shader] - A custom water shader.
 * @property {?Texture} [flowMap=null] - The flow map. If no flow map is assigned, the water flow is defined by `flowDirection`.
 * @property {?Texture} [normalMap0] - The first water normal map.
 * @property {?Texture} [normalMap1] -  The second water normal map.
 **/

export { Water };
